/**
 * Form A, the Composite/Common Application Form.
 *
 * Rule 5, substituted by the Kerala Industrial Single Window Clearance Boards
 * and Industrial Township Area Development (Amendment) Rules, 2021. Fourteen
 * sections, transcribed from the published Gazette in docs/law/rules2021.pdf.
 *
 * The schema is declarative so the applicant screens render from the statutory
 * form rather than from a hand-built copy of it. A field that is not in the
 * Gazette is not in this file.
 */

import { bi, type Bilingual } from "./text.js";
import { statute, type Provenance } from "./provenance.js";

export type FieldType =
  | "text"
  | "longtext"
  | "number"
  | "money_lakh"
  | "date"
  | "email"
  | "phone"
  | "select"
  | "boolean"
  | "table";

export interface FormAField {
  /** Dotted key, e.g. `land.village`. Stable; the payload is stored verbatim. */
  key: string;
  /** The clause letter or numeral as it appears in the Gazette. */
  clause: string;
  label: Bilingual;
  type: FieldType;
  required: boolean;
  /**
   * Set only where the answer has to be yes. A required yes/no question can be
   * validly answered no — "is the land in an industrial area?" is a real
   * question with two real answers — so `required` alone must not demand true.
   * The declaration under section XIV is the one field that must be affirmed.
   */
  mustBeTrue?: boolean;
  options?: Bilingual[];
  help?: Bilingual;
  /** Columns, where the type is a table. */
  columns?: { key: string; label: Bilingual; type: FieldType }[];
}

export interface FormASection {
  /** The Roman numeral as it appears in the Gazette. */
  numeral: string;
  key: string;
  title: Bilingual;
  fields: FormAField[];
}

const f = (
  key: string,
  clause: string,
  en: string,
  ml: string,
  type: FieldType = "text",
  required = false,
  extra: Partial<FormAField> = {},
): FormAField => ({ key, clause, label: bi(en, ml), type, required, ...extra });

export const FORM_A_SECTIONS: readonly FormASection[] = [
  {
    numeral: "I",
    key: "general",
    title: bi("General Information", "പൊതു വിവരങ്ങൾ"),
    fields: [
      f("general.projectName", "(a)", "Project Name", "പദ്ധതിയുടെ പേര്", "text", true),
      f("general.promoterName", "(b)", "Promoter name", "പ്രൊമോട്ടറുടെ പേര്", "text", true),
      f("general.promoterDateOfBirth", "(b)", "Date of birth", "ജനന തീയതി", "date"),
      f("general.promoterEmail", "(b)", "Email id", "ഇമെയിൽ ഐഡി", "email", true),
      f("general.promoterMobile", "(b)", "Mobile number", "മൊബൈൽ നമ്പർ", "phone", true),
      f("general.promoterAadhaar", "(b)", "Aadhaar number", "ആധാർ നമ്പർ"),
      f("general.promoterPan", "(b)", "PAN number", "പാൻ നമ്പർ"),
      f("general.communicationAddress", "(b)", "Communication address", "ബന്ധപ്പെടാനുള്ള വിലാസം", "longtext", true),
      f("general.permanentAddress", "(b)", "Permanent address", "സ്ഥിര വിലാസം", "longtext"),
      f("general.sectorType", "(c)(i)", "Type of sector", "മേഖലയുടെ തരം", "text", true),
      f("general.industrySize", "(c)(ii)", "Size of industry", "വ്യവസായത്തിന്റെ വലുപ്പം", "select", true, {
        options: [
          bi("Micro", "സൂക്ഷ്മം"),
          bi("Small", "ചെറുകിട"),
          bi("Medium", "ഇടത്തരം"),
          bi("Large", "വൻകിട"),
        ],
      }),
      f("general.projectType", "(c)(iii)", "Type of project", "പദ്ധതിയുടെ തരം", "select", true, {
        options: [
          bi("Setting up", "പുതുതായി ആരംഭിക്കൽ"),
          bi("Running", "നടത്തിപ്പ്"),
          bi("Expansion", "വിപുലീകരണം"),
          bi("Diversification", "വൈവിധ്യവൽക്കരണം"),
          bi("Modernisation", "ആധുനികവൽക്കരണം"),
        ],
        help: bi(
          "The five purposes the Act covers.",
          "നിയമം ഉൾക്കൊള്ളുന്ന അഞ്ച് ഉദ്ദേശ്യങ്ങൾ.",
        ),
      }),
      f("general.expectedAnnualTurnover", "(c)(iv)", "Total expected annual turnover", "പ്രതീക്ഷിക്കുന്ന വാർഷിക വിറ്റുവരവ്", "money_lakh"),
      f("general.costOfLand", "(c)(v)", "Cost of land", "ഭൂമിയുടെ വില", "money_lakh"),
      f("general.costOfBuilding", "(c)(vi)", "Cost of building", "കെട്ടിടത്തിന്റെ ചെലവ്", "money_lakh"),
      f("general.costOfMachinery", "(c)(vii)", "Cost of machinery", "യന്ത്രസാമഗ്രികളുടെ വില", "money_lakh"),
      f("general.otherExpenses", "(c)(viii)", "Other expenses", "മറ്റ് ചെലവുകൾ", "money_lakh"),
      f("general.totalCost", "(c)(ix)", "Total cost", "ആകെ ചെലവ്", "money_lakh", true, {
        help: bi(
          "This figure decides which Board hears your application, unless your site is inside a notified industrial area.",
          "നിങ്ങളുടെ സ്ഥലം വിജ്ഞാപിത വ്യവസായ മേഖലയ്ക്കുള്ളിലല്ലെങ്കിൽ, ഈ തുകയാണ് ഏത് ബോർഡ് അപേക്ഷ പരിഗണിക്കുമെന്ന് നിർണ്ണയിക്കുന്നത്.",
        ),
      }),
    ],
  },
  {
    numeral: "II",
    key: "land",
    title: bi("Land Details", "ഭൂമി സംബന്ധിച്ച വിവരങ്ങൾ"),
    fields: [
      f("land.totalExtent", "(a)", "Total extent of the proposed project area", "നിർദ്ദിഷ്ട പദ്ധതി പ്രദേശത്തിന്റെ ആകെ വിസ്തീർണ്ണം", "text", true),
      f("land.inIndustrialArea", "(b)", "Is the land in an industrial area or industrial park?", "ഭൂമി വ്യവസായ മേഖലയിലോ വ്യവസായ പാർക്കിലോ ആണോ?", "boolean", true, {
        help: bi(
          "If it is, section 7 sends your application to the Industrial Area Board for that area, whatever the project costs.",
          "അങ്ങനെയെങ്കിൽ, പദ്ധതിച്ചെലവ് എത്രയായാലും വകുപ്പ് 7 പ്രകാരം അപേക്ഷ ആ മേഖലയുടെ വ്യവസായ മേഖലാ ബോർഡിന് പോകും.",
        ),
      }),
      f("land.industrialAreaId", "(b)", "Which industrial area", "ഏത് വ്യവസായ മേഖല", "select"),
      f("land.ownership", "(c)", "Is the land owned by the applicant, or held on lease or rent?", "ഭൂമി അപേക്ഷകന്റെ ഉടമസ്ഥതയിലാണോ, അതോ പാട്ടത്തിനോ വാടകയ്ക്കോ ആണോ?", "select", true, {
        options: [bi("Owned", "സ്വന്തം"), bi("Lease", "പാട്ടം"), bi("Rent", "വാടക")],
      }),
      f("land.titleHolder", "(d)", "If not owned, name and address of the title holder", "സ്വന്തമല്ലെങ്കിൽ, ഉടമസ്ഥന്റെ പേരും വിലാസവും", "longtext"),
      f("land.deedNumberAndDate", "(e)", "Number and date of the title, lease or rent deed", "ആധാരം, പാട്ടം അല്ലെങ്കിൽ വാടക രേഖയുടെ നമ്പറും തീയതിയും"),
      f("land.subRegistrarOffice", "(f)", "Sub-registrar office where the document is registered", "രേഖ രജിസ്റ്റർ ചെയ്ത സബ് രജിസ്ട്രാർ ഓഫീസ്"),
      f("land.localBodyWard", "(g)", "Ward or division number of the panchayat, municipality or corporation", "പഞ്ചായത്ത്, മുനിസിപ്പാലിറ്റി അല്ലെങ്കിൽ കോർപ്പറേഷന്റെ വാർഡ് നമ്പർ"),
      f("land.developmentAuthority", "(h)", "Name of the development authority, if applicable", "വികസന അതോറിറ്റിയുടെ പേര്, ബാധകമെങ്കിൽ"),
      f("land.village", "(i)", "Village", "വില്ലേജ്", "text", true),
      f("land.taluk", "(j)", "Taluk", "താലൂക്ക്", "text", true),
      f("land.district", "(k)", "District", "ജില്ല", "text", true),
      f("land.surveyNumber", "(l)", "Survey number and subdivision", "സർവേ നമ്പറും ഉപവിഭാഗവും", "text", true),
      f("land.extentHectares", "(m)", "Extent of land in hectares or ares", "ഭൂമിയുടെ വിസ്തീർണ്ണം, ഹെക്ടർ അല്ലെങ്കിൽ ആർ"),
      f("land.effluentArea", "(n)", "Area available for disposal of effluent, if applicable", "മാലിന്യ നിർമ്മാർജ്ജനത്തിന് ലഭ്യമായ സ്ഥലം, ബാധകമെങ്കിൽ"),
      f("land.boundaryEast", "(o)", "Boundary, east", "അതിര്, കിഴക്ക്"),
      f("land.boundaryNorth", "(o)", "Boundary, north", "അതിര്, വടക്ക്"),
      f("land.boundaryWest", "(o)", "Boundary, west", "അതിര്, പടിഞ്ഞാറ്"),
      f("land.boundarySouth", "(o)", "Boundary, south", "അതിര്, തെക്ക്"),
    ],
  },
  {
    numeral: "III",
    key: "buildings",
    title: bi("Details of Factory Buildings", "ഫാക്ടറി കെട്ടിടങ്ങളുടെ വിവരങ്ങൾ"),
    fields: [
      f("buildings.buildingNumber", "(a)", "Building number, if existing", "കെട്ടിട നമ്പർ, നിലവിലുണ്ടെങ്കിൽ"),
      f("buildings.localBody", "(a)", "Ward, grama panchayat, municipality or corporation", "വാർഡ്, ഗ്രാമപഞ്ചായത്ത്, മുനിസിപ്പാലിറ്റി അല്ലെങ്കിൽ കോർപ്പറേഷൻ"),
      f("buildings.road", "(b)", "Name of road or street", "റോഡിന്റെ അല്ലെങ്കിൽ തെരുവിന്റെ പേര്"),
      f("buildings.natureOfConstruction", "(c)", "Nature of construction", "നിർമ്മാണത്തിന്റെ സ്വഭാവം", "select", false, {
        options: [
          bi("New", "പുതിയത്"),
          bi("Reconstruction", "പുനർനിർമ്മാണം"),
          bi("Expansion", "വിപുലീകരണം"),
        ],
      }),
      f("buildings.yearOfEstablishment", "(d)(i)", "Year of establishment", "സ്ഥാപിച്ച വർഷം", "number"),
      f("buildings.buildingPermit", "(d)(ii)", "Building permit number and date of issue by the local authority", "തദ്ദേശ സ്ഥാപനം നൽകിയ കെട്ടിട അനുമതി നമ്പറും തീയതിയും"),
      f("buildings.installationNumber", "(d)(iii)", "Installation number and date", "ഇൻസ്റ്റലേഷൻ നമ്പറും തീയതിയും"),
      f("buildings.purpose", "(e)", "Purpose of construction", "നിർമ്മാണത്തിന്റെ ഉദ്ദേശ്യം", "longtext"),
      f("buildings.plinthArea", "(e)(i)", "Plinth area, floor wise", "പ്ലിന്ത് ഏരിയ, നിലകൾ തിരിച്ച്"),
      f("buildings.carpetArea", "(e)(ii)", "Carpet area, floor wise", "കാർപറ്റ് ഏരിയ, നിലകൾ തിരിച്ച്"),
      f("buildings.height", "(e)(iii)", "Height of the building", "കെട്ടിടത്തിന്റെ ഉയരം"),
      f("buildings.setback", "(e)(iv)", "Set back from boundaries, for proposed construction", "അതിരുകളിൽ നിന്നുള്ള അകലം, നിർദ്ദിഷ്ട നിർമ്മാണത്തിന്"),
      f("buildings.openSpace", "(e)(v)", "Details of open space", "തുറസ്സായ സ്ഥലത്തിന്റെ വിവരങ്ങൾ"),
      f("buildings.estimatedCost", "(e)(vi)", "Estimated cost of the proposed construction", "നിർദ്ദിഷ്ട നിർമ്മാണത്തിന്റെ ഏകദേശ ചെലവ്", "money_lakh"),
    ],
  },
  {
    numeral: "IV",
    key: "utility",
    title: bi("Utility: Power and Water", "സൗകര്യങ്ങൾ: വൈദ്യുതിയും വെള്ളവും"),
    fields: [
      f("utility.electricConnection", "(a)(i)", "Actual requirement of electric connection", "വൈദ്യുതി കണക്ഷന്റെ യഥാർത്ഥ ആവശ്യകത"),
      f("utility.powerPerDayKw", "(a)(ii)", "Total power required per day, in kilowatts", "പ്രതിദിനം ആവശ്യമായ ആകെ വൈദ്യുതി, കിലോവാട്ടിൽ", "number"),
      f("utility.waterPerDayKl", "(b)(i)", "Total water required per day, in kilolitres", "പ്രതിദിനം ആവശ്യമായ ആകെ വെള്ളം, കിലോലിറ്ററിൽ", "number"),
      f("utility.waterSource", "(b)(ii)", "Source of water", "ജലസ്രോതസ്സ്"),
    ],
  },
  {
    numeral: "V",
    key: "capital",
    title: bi("Capital Investment", "മൂലധന നിക്ഷേപം"),
    fields: [
      f("capital.land", "1(a)", "Land", "ഭൂമി", "money_lakh"),
      f("capital.building", "1(b)", "Building", "കെട്ടിടം", "money_lakh"),
      f("capital.machineryImported", "1(c)(i)", "Plant and machinery, imported", "പ്ലാന്റും യന്ത്രസാമഗ്രികളും, ഇറക്കുമതി ചെയ്തത്", "money_lakh"),
      f("capital.machineryIndigenous", "1(c)(ii)", "Plant and machinery, indigenous", "പ്ലാന്റും യന്ത്രസാമഗ്രികളും, തദ്ദേശീയം", "money_lakh"),
      f("capital.miscellaneousAssets", "1(d)", "Miscellaneous assets", "മറ്റ് ആസ്തികൾ", "money_lakh"),
      f("capital.incidentalExpenses", "1(e)", "Incidental expenses", "അനുബന്ധ ചെലവുകൾ", "money_lakh"),
      f("capital.preOperativeExpenses", "1(f)", "Preliminary and pre-operative expenses", "പ്രാരംഭ ചെലവുകൾ", "money_lakh"),
      f("capital.marginMoney", "1(g)", "Margin money for working capital", "പ്രവർത്തന മൂലധനത്തിനുള്ള മാർജിൻ തുക", "money_lakh"),
      f("capital.workingCapital", "1(h)", "Working capital at full capacity utilisation", "പൂർണ്ണ ശേഷിയിലുള്ള പ്രവർത്തന മൂലധനം", "money_lakh"),
      f("capital.existingFixedAssets", "1(i)", "For existing units, total investment in fixed assets", "നിലവിലുള്ള യൂണിറ്റുകൾക്ക്, സ്ഥിര ആസ്തികളിലെ ആകെ നിക്ഷേപം", "money_lakh"),
    ],
  },
  {
    numeral: "VI",
    key: "rawMaterials",
    title: bi("Raw Materials", "അസംസ്കൃത വസ്തുക്കൾ"),
    fields: [
      f("rawMaterials.indigenous", "(a)", "Indigenous raw materials", "തദ്ദേശീയ അസംസ്കൃത വസ്തുക്കൾ", "table", false, {
        columns: [
          { key: "name", label: bi("Name of item", "വസ്തുവിന്റെ പേര്"), type: "text" },
          { key: "quantity", label: bi("Quantity", "അളവ്"), type: "text" },
          { key: "costPerUnit", label: bi("Cost per unit", "യൂണിറ്റ് വില"), type: "number" },
          { key: "totalCost", label: bi("Total cost", "ആകെ വില"), type: "number" },
          { key: "source", label: bi("Source", "സ്രോതസ്സ്"), type: "text" },
        ],
      }),
      f("rawMaterials.imported", "(b)", "Imported raw materials", "ഇറക്കുമതി ചെയ്ത അസംസ്കൃത വസ്തുക്കൾ", "table", false, {
        columns: [
          { key: "name", label: bi("Name of item", "വസ്തുവിന്റെ പേര്"), type: "text" },
          { key: "quantity", label: bi("Quantity", "അളവ്"), type: "text" },
          { key: "costPerUnit", label: bi("Cost per unit", "യൂണിറ്റ് വില"), type: "number" },
          { key: "totalCost", label: bi("Total cost", "ആകെ വില"), type: "number" },
          { key: "source", label: bi("Source", "സ്രോതസ്സ്"), type: "text" },
        ],
      }),
    ],
  },
  {
    numeral: "VII",
    key: "products",
    title: bi("Finished Products", "പൂർത്തിയായ ഉൽപ്പന്നങ്ങൾ"),
    fields: [
      f("products.items", "", "Finished products, including by-products and intermediary products", "ഉപോൽപ്പന്നങ്ങളും ഇടനില ഉൽപ്പന്നങ്ങളും ഉൾപ്പെടെ", "table", false, {
        columns: [
          { key: "item", label: bi("Item", "ഉൽപ്പന്നം"), type: "text" },
          { key: "totalProduct", label: bi("Total product", "ആകെ ഉൽപ്പാദനം"), type: "text" },
          { key: "unitPrice", label: bi("Unit price, ex-factory", "യൂണിറ്റ് വില, ഫാക്ടറി വില"), type: "number" },
          { key: "totalValue", label: bi("Total value", "ആകെ മൂല്യം"), type: "number" },
          { key: "use", label: bi("Use", "ഉപയോഗം"), type: "text" },
        ],
      }),
    ],
  },
  {
    numeral: "VIII",
    key: "finance",
    title: bi("Means of Finance", "ധനസമാഹരണ മാർഗ്ഗങ്ങൾ"),
    fields: [
      f("finance.banks", "(a)", "Banks and financial institutions", "ബാങ്കുകളും ധനകാര്യ സ്ഥാപനങ്ങളും", "money_lakh"),
      f("finance.capitalContribution", "(b)", "Capital contribution", "മൂലധന വിഹിതം", "money_lakh"),
      f("finance.fdi", "(c)", "Foreign direct investment", "വിദേശ നേരിട്ടുള്ള നിക്ഷേപം", "money_lakh"),
      f("finance.others", "(d)", "Others", "മറ്റുള്ളവ", "money_lakh"),
      f("finance.governmentGrant", "(e)", "Grant from Government", "സർക്കാർ ധനസഹായം", "money_lakh"),
    ],
  },
  {
    numeral: "IX",
    key: "employment",
    title: bi("Proposed Employment Opportunity", "നിർദ്ദിഷ്ട തൊഴിലവസരം"),
    fields: [
      f("employment.directMale", "", "Direct, male", "നേരിട്ട്, പുരുഷൻ", "number"),
      f("employment.directFemale", "", "Direct, female", "നേരിട്ട്, സ്ത്രീ", "number"),
      f("employment.directOther", "", "Direct, other", "നേരിട്ട്, മറ്റുള്ളവർ", "number"),
      f("employment.indirectMale", "", "Indirect, male", "പരോക്ഷം, പുരുഷൻ", "number"),
      f("employment.indirectFemale", "", "Indirect, female", "പരോക്ഷം, സ്ത്രീ", "number"),
      f("employment.indirectOther", "", "Indirect, other", "പരോക്ഷം, മറ്റുള്ളവർ", "number"),
    ],
  },
  {
    numeral: "X",
    key: "enterprise",
    title: bi("Enterprise Details", "സ്ഥാപന വിവരങ്ങൾ"),
    fields: [
      f("enterprise.entityName", "(a)", "Entity name", "സ്ഥാപനത്തിന്റെ പേര്", "text", true),
      f("enterprise.lineOfActivity", "(b)", "Line of activity", "പ്രവർത്തന മേഖല", "text", true),
      f("enterprise.promoterOrMd", "(c)", "Name of promoter or managing director", "പ്രൊമോട്ടറുടെ അല്ലെങ്കിൽ മാനേജിംഗ് ഡയറക്ടറുടെ പേര്"),
      f("enterprise.tin", "(d)", "TIN number", "ടിൻ നമ്പർ"),
      f("enterprise.vat", "(e)", "VAT number", "വാറ്റ് നമ്പർ"),
      f("enterprise.cst", "(f)", "CST number", "സിഎസ്ടി നമ്പർ"),
      f("enterprise.centralExcise", "(g)", "Central excise registration number", "കേന്ദ്ര എക്സൈസ് രജിസ്ട്രേഷൻ നമ്പർ"),
      f("enterprise.iec", "(h)", "IEC number", "ഐഇസി നമ്പർ"),
      f("enterprise.gstin", "(i)", "GSTIN registration number", "ജിഎസ്ടിഐഎൻ രജിസ്ട്രേഷൻ നമ്പർ"),
      f("enterprise.rocNumber", "(j)", "ROC number", "ആർഒസി നമ്പർ"),
      f("enterprise.rocDate", "(k)", "ROC registration date", "ആർഒസി രജിസ്ട്രേഷൻ തീയതി", "date"),
      f("enterprise.stdCode", "(l)", "STD code", "എസ്ടിഡി കോഡ്"),
      f("enterprise.landline", "(m)", "Land phone number", "ലാൻഡ് ഫോൺ നമ്പർ"),
      f("enterprise.fax", "(n)", "Fax", "ഫാക്സ്"),
      f("enterprise.website", "(o)", "Official website URL", "ഔദ്യോഗിക വെബ്സൈറ്റ്"),
      f("enterprise.partners", "(p)", "Partner details", "പങ്കാളികളുടെ വിവരങ്ങൾ", "table", false, {
        columns: [
          { key: "type", label: bi("Type", "തരം"), type: "text" },
          { key: "name", label: bi("Name", "പേര്"), type: "text" },
          { key: "gender", label: bi("Gender", "ലിംഗം"), type: "text" },
          { key: "dateOfBirth", label: bi("Date of birth", "ജനന തീയതി"), type: "date" },
          { key: "mobile", label: bi("Mobile number", "മൊബൈൽ നമ്പർ"), type: "phone" },
          { key: "email", label: bi("Email id", "ഇമെയിൽ ഐഡി"), type: "email" },
        ],
      }),
      f("enterprise.bankName", "(q)(i)", "Name of bank", "ബാങ്കിന്റെ പേര്"),
      f("enterprise.bankAccountNumber", "(q)(ii)", "Account number", "അക്കൗണ്ട് നമ്പർ"),
      f("enterprise.bankAccountType", "(q)(iii)", "Type of account", "അക്കൗണ്ടിന്റെ തരം"),
      f("enterprise.bankAccountHolder", "(q)(iv)", "Account holder", "അക്കൗണ്ട് ഉടമ"),
      f("enterprise.bankIfsc", "(q)(v)", "IFS code", "ഐഎഫ്എസ് കോഡ്"),
      f("enterprise.bankBranch", "(q)(vi)", "Branch name", "ബ്രാഞ്ചിന്റെ പേര്"),
    ],
  },
  {
    numeral: "XI",
    key: "addresses",
    title: bi("Communication Address", "ബന്ധപ്പെടാനുള്ള വിലാസം"),
    fields: [
      f("addresses.registeredState", "(a)(i)", "Registered office, state", "രജിസ്റ്റർ ചെയ്ത ഓഫീസ്, സംസ്ഥാനം"),
      f("addresses.registeredDistrict", "(a)(ii)", "Registered office, district", "രജിസ്റ്റർ ചെയ്ത ഓഫീസ്, ജില്ല"),
      f("addresses.registeredStreet", "(a)(iii)", "Registered office, house or street number", "രജിസ്റ്റർ ചെയ്ത ഓഫീസ്, വീട് അല്ലെങ്കിൽ തെരുവ് നമ്പർ"),
      f("addresses.registeredPost", "(a)(iv)", "Registered office, post office with PIN code", "രജിസ്റ്റർ ചെയ്ത ഓഫീസ്, പോസ്റ്റ് ഓഫീസും പിൻ കോഡും"),
      f("addresses.headState", "(b)(i)", "Head office, state", "ഹെഡ് ഓഫീസ്, സംസ്ഥാനം"),
      f("addresses.headDistrict", "(b)(ii)", "Head office, district", "ഹെഡ് ഓഫീസ്, ജില്ല"),
      f("addresses.headStreet", "(b)(iii)", "Head office, house or street number", "ഹെഡ് ഓഫീസ്, വീട് അല്ലെങ്കിൽ തെരുവ് നമ്പർ"),
      f("addresses.headPost", "(b)(iv)", "Head office, post office with PIN code", "ഹെഡ് ഓഫീസ്, പോസ്റ്റ് ഓഫീസും പിൻ കോഡും"),
    ],
  },
  {
    numeral: "XII",
    key: "misc",
    title: bi("Miscellaneous Details", "മറ്റ് വിവരങ്ങൾ"),
    fields: [
      f("misc.expectedStartDate", "(a)", "Expected date of starting the project", "പദ്ധതി ആരംഭിക്കുന്ന പ്രതീക്ഷിത തീയതി", "date"),
      f("misc.expectedTrialDate", "(b)", "Expected date of starting trial production", "പരീക്ഷണ ഉൽപ്പാദനം ആരംഭിക്കുന്ന പ്രതീക്ഷിത തീയതി", "date"),
      f("misc.expectedOperationDate", "(c)", "Expected date of starting operation", "പ്രവർത്തനം ആരംഭിക്കുന്ന പ്രതീക്ഷിത തീയതി", "date"),
      f("misc.effluentQuantity", "(d)", "Effluent quantity, cubic metres per annum", "മാലിന്യ ജലത്തിന്റെ അളവ്, വർഷത്തിൽ ക്യുബിക് മീറ്റർ", "number"),
      f("misc.solidWasteQuantity", "(e)", "Solid waste quantity, units per day", "ഖരമാലിന്യത്തിന്റെ അളവ്, പ്രതിദിനം", "number"),
    ],
  },
  {
    numeral: "XIII",
    key: "clearances",
    title: bi(
      "Licences, approvals, clearances, no objection certificates and permits required",
      "ആവശ്യമായ ലൈസൻസുകൾ, അനുമതികൾ, എൻ.ഒ.സി, പെർമിറ്റുകൾ",
    ),
    fields: [
      f("clearances.requested", "", "Clearances sought", "ആവശ്യപ്പെടുന്ന അനുമതികൾ", "table", true, {
        help: bi(
          "Choose every clearance you need. This list is what the Board circulates to the authorities.",
          "നിങ്ങൾക്ക് ആവശ്യമായ എല്ലാ അനുമതികളും തിരഞ്ഞെടുക്കുക. ഈ പട്ടികയാണ് ബോർഡ് അധികാരികൾക്ക് അയയ്ക്കുന്നത്.",
        ),
        columns: [
          { key: "clearanceTypeId", label: bi("Clearance", "അനുമതി"), type: "select" },
          { key: "purpose", label: bi("Purpose", "ഉദ്ദേശ്യം"), type: "text" },
        ],
      }),
    ],
  },
  {
    numeral: "XIV",
    key: "declaration",
    title: bi("Declaration", "സത്യപ്രസ്താവന"),
    fields: [
      f("declaration.agreed", "", "I declare that all facts stated above are true and correct.", "മുകളിൽ പറഞ്ഞ എല്ലാ വസ്തുതകളും സത്യവും ശരിയുമാണെന്ന് ഞാൻ പ്രഖ്യാപിക്കുന്നു.", "boolean", true, { mustBeTrue: true }),
      f("declaration.name", "", "Name of the applicant", "അപേക്ഷകന്റെ പേര്", "text", true),
    ],
  },
];

export const FORM_A_PROVENANCE: Provenance = statute(
  "r.5, Form A substituted 2021",
  "Transcribed from the Kerala Gazette notification substituting Form A, fourteen sections.",
);

export const FORM_A_FIELDS: readonly FormAField[] = FORM_A_SECTIONS.flatMap((s) => s.fields);

export function formAField(key: string): FormAField | undefined {
  return FORM_A_FIELDS.find((f) => f.key === key);
}

export interface FormAValidationError {
  key: string;
  message: Bilingual;
}

/** Every field the Gazette marks as required must be present and non-empty. */
export function validateFormA(payload: Record<string, unknown>): FormAValidationError[] {
  const errors: FormAValidationError[] = [];
  for (const field of FORM_A_FIELDS) {
    if (!field.required) continue;
    const value = payload[field.key];
    const answered =
      value !== undefined &&
      value !== null &&
      !(typeof value === "string" && value.trim().length === 0) &&
      !(Array.isArray(value) && value.length === 0);

    if (!answered) {
      errors.push({
        key: field.key,
        message: bi(`${field.label.en} is required.`, `${field.label.ml} നിർബന്ധമാണ്.`),
      });
      continue;
    }

    if (field.mustBeTrue === true && value !== true) {
      errors.push({
        key: field.key,
        message: bi(
          `${field.label.en} must be agreed before the application can be filed.`,
          `അപേക്ഷ സമർപ്പിക്കുന്നതിന് മുൻപ് ${field.label.ml} സമ്മതിക്കണം.`,
        ),
      });
    }
  }
  // A site said to be inside an industrial area has to say which one, because
  // that is what section 7 routes on.
  if (payload["land.inIndustrialArea"] === true) {
    const area = payload["land.industrialAreaId"];
    if (typeof area !== "string" || area.trim().length === 0) {
      errors.push({
        key: "land.industrialAreaId",
        message: bi(
          "Say which industrial area the site is in. Section 7 routes the application on that.",
          "സ്ഥലം ഏത് വ്യവസായ മേഖലയിലാണെന്ന് പറയുക. വകുപ്പ് 7 അതനുസരിച്ചാണ് അപേക്ഷ കൈമാറുന്നത്.",
        ),
      });
    }
  }
  return errors;
}
